import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Loader2 } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { OrderRecord } from "@/types";

interface Props {
  order: OrderRecord | null;
  open: boolean;
  canIssue: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const pdfUrl = (id: string) => `/api/SalesOrder/ProformaPdf/${id}`;

/** Issue (or re-issue) the proforma invoice for an advance, then open it */
export function ProformaDialog({ order, open, canIssue, onClose, onSaved }: Props) {
  const { success, error } = useToast();
  const [advance, setAdvance] = useState("100");
  const [days, setDays] = useState("15");
  const [saving, setSaving] = useState(false);
  const issued = !!order?.proforma_number;

  useEffect(() => {
    if (!open || !order) return;
    setAdvance(order.proforma_advance_pct != null ? String(Number(order.proforma_advance_pct)) : "100");
    setDays("15");
  }, [open, order]);

  const issue = async () => {
    const a = Number(advance);
    const d = Number(days);
    if (!(a >= 0 && a <= 100)) return error("Check the advance", "Enter a percentage from 0 to 100");
    if (!(Number.isInteger(d) && d >= 1 && d <= 180)) return error("Check the validity", "Enter 1 to 180 days");
    setSaving(true);
    try {
      await api.post(`/api/SalesOrder/Proforma/${order!.id}`, { advance_pct: a, valid_days: d });
      success(issued ? "Proforma invoice re-issued" : "Proforma invoice issued", "");
      onSaved();
      window.open(pdfUrl(order!.id), "_blank");
      onClose();
    } catch (err: any) {
      error("Could not issue the proforma", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const grand = Number(order?.grand_total || 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Proforma invoice</DialogTitle>
          <DialogDescription>
            {issued
              ? `${order?.proforma_number}, issued ${order?.proforma_date}. Re-issuing keeps the number and shows the order's current lines.`
              : "For the buyer to pay an advance. The number is kept once issued, so their payment can quote it."}
          </DialogDescription>
        </DialogHeader>
        {canIssue && (
          <div className="grid grid-cols-2 gap-3 py-1">
            <div className="space-y-1.5">
              <Label>Advance (%)</Label>
              <Input type="number" min={0} max={100} step="any" value={advance} onChange={(e) => setAdvance(e.target.value)} />
              <p className="text-[11px] text-muted-foreground">About {formatINR(Math.round((grand * Number(advance || 0)) / 100))} with GST</p>
            </div>
            <div className="space-y-1.5">
              <Label>Valid for (days)</Label>
              <Input type="number" min={1} max={180} value={days} onChange={(e) => setDays(e.target.value)} />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          {issued && (
            <Button variant="outline" onClick={() => window.open(pdfUrl(order!.id), "_blank")} disabled={saving}>
              <FileText className="size-4 mr-1.5" /> Open {order?.proforma_number}
            </Button>
          )}
          {canIssue && (
            <Button onClick={issue} disabled={saving}>
              {saving && <Loader2 className="size-4 animate-spin mr-1.5" />}
              {issued ? "Re-issue and open" : "Issue and open"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
